import React from 'react';
import moment from 'moment';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { getInputFormat } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import SlideForm from '../common/forms/SlideForm';
import {
    Input, onFieldChange, toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { LongTermStatusType } from '../longTermStatus/LongTermStatus';
import { EmployeeLongTermStatus } from './EmployeeLongTermStatus';

export class EmployeeLongTermStatusForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            errors: {},
            endDateError: '',
            employeeLongTermStatus:
                this.props.employeeLongTermStatus
                ?? new EmployeeLongTermStatus(),
            longTermStatuses: [],
            isValidated: false,
            formTitle: '',
            loading: false,
            saving: false,
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onLongTermStatusChanged = this.onLongTermStatusChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onDelete = async () => {
        const { employeeLongTermStatus } = this.state;

        const response = await httpClient
            .delete(
                ApiRoutes.employeeLongTermStatus.delete(
                    employeeLongTermStatus.id,
                ),
            )
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toasty.success(
                'Employee Long Term Status deleted',
                'Employee Long Term Status deleted successfully.',
            );

            this.onClose(response);
        }
    };

    onLongTermStatusChanged = (selection) => {
        const { employeeLongTermStatus } = this.state;

        employeeLongTermStatus.longTermStatusId = selection.id;

        this.setState({ employeeLongTermStatus });
    };

    handleDateChange = (e) => {
        const { name, value } = e.target;
        const { employeeLongTermStatus } = this.state;

        this.setState(
            {
                employeeLongTermStatus: {
                    ...employeeLongTermStatus,
                    [name]: value, // Dynamically update either startDate or endDate
                },
            },
            this.validateDates, // Validate after state update
        );
    };

    validateDates = () => {
        const { startDate, endDate } = this.state.employeeLongTermStatus;

        let endDateError = '';

        if (moment(endDate).isValid() && moment(startDate).isValid()) {
            if (moment(endDate).isBefore(moment(startDate))) {
                endDateError = 'End Date cannot be earlier than Start Date.';
            }
        }

        this.setState({ endDateError });
    };

    onSubmit = async () => {
        const { employeeLongTermStatus, endDateError } = this.state;

        if (endDateError) {
            return;
        }

        if (!employeeLongTermStatus.endDate) {
            employeeLongTermStatus.endDate = null;
        }

        this.setState({ errors: {}, endDateError: '', saving: true });

        const isExisting = Boolean(
            employeeLongTermStatus.id && employeeLongTermStatus.id > 0,
        );

        const url = isExisting
            ? ApiRoutes.employeeLongTermStatus.update(employeeLongTermStatus.id)
            : ApiRoutes.employeeLongTermStatus.create();

        const fetchType = isExisting
            ? httpClient.types.put
            : httpClient.types.post;

        const response = await httpClient.andGetResponse(
            fetchType,
            url,
            employeeLongTermStatus,
            'Error Saving Long-Term Status',
            () => this.setState({ saving: false }),
        );

        if (response && !isNaN(response)) {
            this.onClose(response);

            const description = isExisting ? 'updated' : 'created';
            toasty.success(
                `Employee Long Term Status ${description}`,
                `Employee Long Term Status ${description} successfully.`,
            );

            this.props.onSaveCallback();
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    isEndDateRequired = () => {
        const { employeeLongTermStatus, longTermStatuses } = this.state;

        if (
            !employeeLongTermStatus
            || !employeeLongTermStatus.longTermStatusId
        ) {
            return false;
        }

        const longTermStatus = longTermStatuses.find(
            (l) => l.id == employeeLongTermStatus.longTermStatusId,
        );

        return Boolean(longTermStatus && longTermStatus.requiresEndDate);
    };

    open = (statusOrEmployeeId) => {
        try {
            const isNew = typeof statusOrEmployeeId === 'number';

            let employeeLongTermStatus;

            if (isNew) {
                employeeLongTermStatus = new EmployeeLongTermStatus();
                employeeLongTermStatus.employeeId = statusOrEmployeeId;
                employeeLongTermStatus.effectiveDate = getInputFormat(new Date());
            } else {
                employeeLongTermStatus = { ...statusOrEmployeeId };
            }

            // Format dates
            employeeLongTermStatus.startDate = getInputFormat(
                employeeLongTermStatus.startDate,
            );
            employeeLongTermStatus.endDate = getInputFormat(
                employeeLongTermStatus.endDate,
            );

            if (isNew) {
                employeeLongTermStatus.startDate = moment().format('YYYY-MM-DD');
            }
            // Set state and open form
            this.setState({
                employeeLongTermStatus,
                formTitle: isNew ? 'New Long Term Status' : 'Edit Long Term Status',
            });
            this.props.toggleShow(true);
            this.context.setFormOpened(true);
        } catch (error) {
            console.error('Error opening long term status form:', error);
            toasty.error('Failed to open long term status form');
        }
    };

    populateState = async () => {
        const [longTermStatuses] = await Promise.all([
            httpClient.js(ApiRoutes.longTermStatus.byTypeId(LongTermStatusType.Employee)),
        ]);

        this.setState({ longTermStatuses, loading: false });
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            employeeLongTermStatus: new EmployeeLongTermStatus(),
            errors: {},
            endDateError: '',
            isValidated: false,
            validationMessage: null,
        });
    };

    render() {
        const {
            errors,
            validationMessage,
            employeeLongTermStatus,
            longTermStatuses,
            isValidated,
            endDateError,
            loading,
            saving,
            formTitle,
        } = this.state;
        const { show } = this.props;

        if (!employeeLongTermStatus) {
            return '';
        }

        const isEndDateRequired = this.isEndDateRequired();

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="employeeLongTermStatusForm"
                formIcon={faCalendarTimes}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(val) => this.setState({ isValidated: val })}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onDelete={employeeLongTermStatus.id ? this.onDelete : undefined}
                errors={errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                saveDisabled={saving}
                entityId={employeeLongTermStatus.id}
            >
                <ValidatedSelect
                    className="w-auto"
                    isClearable={false}
                    id="longTermStatusesDropdown"
                    name="employeeLongTermStatus.longTermStatusId"
                    label="Long Term Status"
                    required
                    value={
                        longTermStatuses.find(
                            (i) => i.id
                                == employeeLongTermStatus.longTermStatusId,
                        ) ?? ''
                    }
                    options={longTermStatuses ?? []}
                    onChange={this.onLongTermStatusChanged}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    error="A long term status is required."
                    isDisabled={Boolean(
                        employeeLongTermStatus.id,
                    )}
                />
                <Input
                    label="Start Date"
                    id="startDate"
                    required
                    type="date"
                    name="startDate"
                    value={employeeLongTermStatus.startDate || ''}
                    onChange={this.handleDateChange}
                    error="Start Date is required."
                />
                <Input
                    label="End Date"
                    id="endDate"
                    type="date"
                    required={isEndDateRequired}
                    name="endDate"
                    value={employeeLongTermStatus.endDate || ''}
                    onChange={this.handleDateChange}
                    error={endDateError}
                />
            </SlideForm>
        );
    }
}
